/**
 * SybaSoil PDP offer-area client injector — full MainProduct section swap
 * ----------------------------------------------------------------------
 * Problem: the SybaSoil tier-card offer module only renders on the PDP when
 * Intelligems' server-side Template Change swaps product.sybasoil →
 * product.sybasoil_offer_a-b. That server-side swap is unreliable / currently
 * not firing, so Variant-B shoppers who land directly on a SybaSoil PDP do
 * NOT see the offer (quick-add already works because assets/quick-add.js
 * client-fetches the Variant-B template gated on window.SybaIG).
 *
 * The offer template (product.sybasoil_offer_a-b) does NOT only add the
 * .sybasoil-offer block — it also re-configures the variant_picker block
 * (hide_10l:true, hide_15l:true, show_discount_badge:false, card_max_width:90)
 * and disables the quantity_selector block. Grafting only the offer block onto
 * the standard section would re-implement those server-driven differences as
 * hardcoded client CSS/JS — a dual-write drift hazard. Instead this script
 * delivers the offer template's *server-rendered product section* to
 * Variant-B by REPLACING THE WHOLE MainProduct section, exactly mirroring how
 * assets/quick-add.js already does it for the quick-add modal
 * (fetch ?view=sybasoil_offer_a-b → DOMParser → section[id^="MainProduct-"]
 * → setInnerHTML with <script>-node recreation → re-init Kaching / payment
 * buttons / XR / gallery / variant picker / preselect 5L).
 *
 * Because we REPLACE the live section's content with the offer section's
 * content for the SAME product + SAME section.id, the swapped-in element IDs
 * are identical to (and uniquely replace) the old ones — there is no
 * duplicate-ID collision the way there is in quick-add (where the modal
 * coexists with the original section). The quick-add preventDuplicatedIDs()
 * trick is therefore intentionally NOT applied here; doing so would rename
 * data-section/IDs and desync the swapped product form from its own scripts.
 *
 * Control parity is absolute: if SybaIG is missing, the test is pending, or
 * the visitor is Control, this script is a strict no-op — the native
 * server-rendered PDP is left byte-for-byte untouched. Any error anywhere
 * (fetch failure, non-SybaSoil 404, parse error, missing section) also
 * degrades to the untouched native PDP.
 *
 * Idempotent: if the IG server-side template change DID fire (the offer is
 * already in the DOM) OR a prior run already swapped, this does nothing —
 * never double-fetches, never double-swaps.
 *
 * Reuses the exact <script>-node-recreation trick from
 * assets/quick-add.js setInnerHTML(): nodes inserted via innerHTML do not
 * execute their <script> children, so each <script> is recreated as a fresh
 * element. This guarantees the inline config scripts run (setting the
 * window.sybasoil* globals + the pot-calculator), assets/sybasoil-offer-tier-cards.js
 * loads & binds to the swapped product form, and the section's own variant /
 * gallery scripts re-execute.
 */
(function () {
  'use strict';

  // ===================================================================
  // Issue 3 — swap-INDEPENDENT Variant-B picker hardening (10L/15L lock)
  // -------------------------------------------------------------------
  // The #164 section swap eventually replaces the native SybaSoil PDP
  // with the offer template, whose variant_picker hides 10L/15L
  // (hide_10l:true, hide_15l:true). But there is a window BEFORE that
  // swap lands (FOUC) — and pages where the swap is delayed or never
  // fires — during which a Variant-B shopper can still see and click the
  // 10L / 15L cards on a SybaSoil mix. The merchant approved locking
  // those sizes for the offer line (SybaSoil mixes only).
  //
  // This layer is fully independent of the section swap: it runs ASAP
  // from the header <script defer> on EVERY page, is strictly gated to
  // Variant-B + a SybaSoil product, tags the 10L/15L cards with a hide
  // class (+ disables their radios so they can never be submitted), and
  // ships a matching CSS rule injected once. If the currently selected
  // (or first) variant is a hidden size it force-selects the first
  // VISIBLE size so the product form's hidden input[name=id], price and
  // ATC never point at a hidden variant. It re-applies on DOM mutation
  // so it survives the #164 swap re-rendering the picker. Idempotent.
  //
  // Control / test-inactive / non-SybaSoil → NO marker class, NO style,
  // NO tagging, NO forced selection: the native PDP is byte-for-byte
  // untouched (Control parity is absolute, same as the swap injector).
  // ===================================================================

  var HIDE_SIZES = ['10l', '15l'];
  var HIDE_CARD_CLASS = 'syba-b-hidden-size';
  var HTML_MARKER = 'syba-b-offer';
  var STYLE_ID = 'syba-b-offer-hardening-style';

  // Synchronous Variant-B gate — mirrors the swap injector's Gate 2
  // exactly. false for Control / pending / SDK-not-ready / error.
  function isVariantB() {
    try {
      return !!(window.SybaIG && window.SybaIG.checkVariantBSync());
    } catch (e) {
      return false;
    }
  }

  // Products that sit on a `sybasoil*` template for page-layout reasons but
  // are NOT part of the SybaSoil "buy more, save more + free gift" A/B offer.
  // The Soil Discovery / try-out bundle (`/products/try-out-bundle`) renders
  // on `product.sybasoil_try-out-offer` purely for its layout, but its
  // productType is "Care Bundles" with a single "Default Title" variant — it
  // can never satisfy the discount Function's "Houseplants Mixes" + 5L/20L
  // rule, and it contributes 0 qualifying units so the free-gift abuse guard
  // would keep the gift unfree. Injecting the offer there is therefore a
  // broken promise. Exclude such products from EVERY Variant-B offer path.
  // Match by Shopify product handle (handles are NOT localized; only the
  // optional leading `/<locale>/` path prefix is). Keep this list in sync
  // with the twin guard in assets/quick-add.js (sybasoilFetchUrl).
  var SYBA_OFFER_EXCLUDED_HANDLES = ['try-out-bundle'];
  function isSybaOfferExcludedProduct() {
    try {
      var m = location.pathname.match(/\/products\/([^/?#]+)/);
      var handle = m && m[1] ? decodeURIComponent(m[1]).toLowerCase() : '';
      return handle !== '' && SYBA_OFFER_EXCLUDED_HANDLES.indexOf(handle) !== -1;
    } catch (e) {
      return false;
    }
  }

  // Swap-independent SybaSoil-product signal. window._template.suffix is
  // set server-side in layout/theme.liquid for every page load and is
  // available before DOMContentLoaded. SybaSoil mixes render on the
  // `product.sybasoil*` template family (sybasoil, sybasoil_offer_a-b,
  // sybasoil_ab_variant, sybasoil_try-out-offer, bvariation_sybasoil_*).
  // Matching the `sybasoil` substring covers the pre-swap native page
  // (suffix `sybasoil`) AND is harmless on the offer template (where the
  // 10L/15L cards are already absent server-side — nothing to tag).
  // Explicitly-excluded handles (see SYBA_OFFER_EXCLUDED_HANDLES) are never
  // treated as a SybaSoil offer product even if their template matches.
  function isSybaSoilProduct() {
    try {
      if (isSybaOfferExcludedProduct()) return false;
      var t = window._template;
      if (!t || t.name !== 'product') return false;
      var suffix = (t.suffix || '').toLowerCase();
      return suffix.indexOf('sybasoil') !== -1;
    } catch (e) {
      return false;
    }
  }

  // First whitespace-separated token of a radio value, lowercased
  // (matches snippets/product-variant-options.liquid hide_token logic:
  // value="5L"/"10L"/"15L"/"20L" → '5l'/'10l'/'15l'/'20l').
  function sizeToken(input) {
    return ((input && input.value) || '').trim().split(/\s+/)[0].toLowerCase();
  }

  function ensureHardeningStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      // Primary: JS-tagged cards (works in every browser incl. Edge).
      'html.' + HTML_MARKER + ' .sybot-variant-card.' + HIDE_CARD_CLASS +
      '{display:none !important;}' +
      // Fallback: :has() for browsers that support it, in case a freshly
      // injected card is briefly visible before the JS tagging pass runs
      // (still strictly scoped to the Variant-B marker + SybaSoil sizes).
      'html.' + HTML_MARKER +
      ' .sybot-variant-card:has(> input[type="radio"][value="10L" i]),' +
      'html.' + HTML_MARKER +
      ' .sybot-variant-card:has(> input[type="radio"][value="15L" i])' +
      '{display:none !important;}';
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  }

  // Tag + disable every 10L/15L card; force-select the first visible
  // size if the active selection is (or would default to) a hidden one.
  // Scope defaults to document so it covers the native picker AND the
  // swapped-in offer section's picker. Idempotent — re-running only adds
  // already-absent classes / re-checks an already-correct radio.
  function hardenPicker(scope) {
    try {
      var rootEl = scope || document;
      var cards = rootEl.querySelectorAll('.sybot-variant-card');
      if (!cards.length) return;

      var checkedHidden = false;
      var firstVisibleRadio = null;
      var firstVisible5LRadio = null;
      var anyTagged = false;

      cards.forEach(function (card) {
        var input = card.querySelector('input[type="radio"]');
        if (!input) return;
        var token = sizeToken(input);
        var hide = HIDE_SIZES.indexOf(token) !== -1;

        if (hide) {
          if (!card.classList.contains(HIDE_CARD_CLASS)) {
            card.classList.add(HIDE_CARD_CLASS);
          }
          // Belt-and-braces: a disabled radio cannot be submitted with
          // the form and cannot be reached by keyboard, independent of
          // the CSS. (The card is display:none anyway, but if the style
          // ever failed to inject this still prevents selection.)
          input.disabled = true;
          input.setAttribute('aria-hidden', 'true');
          input.tabIndex = -1;
          if (input.checked) checkedHidden = true;
          anyTagged = true;
        } else {
          if (!firstVisibleRadio) firstVisibleRadio = input;
          if (!firstVisible5LRadio && token === '5l') firstVisible5LRadio = input;
        }
      });

      // Nothing to hide on this product (e.g. Aroid has no 10L/15L) →
      // leave the picker exactly as-is. Still a no-op for Control.
      if (!anyTagged) return;

      // If the form would point at a hidden variant (its radio is
      // checked, OR no visible radio is checked at all), move the
      // selection to the first visible size — prefer 5L — and fire
      // `change` so variant-radios cascades it into the product form's
      // hidden input[name=id], the price block and the ATC button.
      var target = firstVisible5LRadio || firstVisibleRadio;
      if (!target) return;
      var someVisibleChecked = false;
      cards.forEach(function (card) {
        if (card.classList.contains(HIDE_CARD_CLASS)) return;
        var i = card.querySelector('input[type="radio"]');
        if (i && i.checked) someVisibleChecked = true;
      });

      if ((checkedHidden || !someVisibleChecked) && !target.checked) {
        var fieldset = target.closest('fieldset');
        if (fieldset) {
          fieldset
            .querySelectorAll('input[type="radio"]:checked')
            .forEach(function (r) { r.checked = false; });
        }
        target.checked = true;
        if (
          window.customElements &&
          typeof customElements.upgrade === 'function'
        ) {
          try { customElements.upgrade(rootEl); } catch (e) { /* ignore */ }
        }
        // Defer so the variant-radios custom element has wired its
        // change listener before we dispatch (same reasoning as the
        // swap injector's preselect5L deferral).
        setTimeout(function () {
          try {
            target.dispatchEvent(new Event('change', { bubbles: true }));
          } catch (e) { /* ignore */ }
        }, 0);
      }
    } catch (e) {
      /* hardening is best-effort — never break the native PDP */
    }
  }

  function hardenVariantBPicker() {
    try {
      // Strict gate: Variant-B + SybaSoil product only. Any other state
      // (Control, pending, SDK-not-ready, non-SybaSoil, not a PDP) →
      // total no-op, native PDP byte-for-byte untouched.
      if (!isVariantB() || !isSybaSoilProduct()) return;
      if (!document.querySelector('section[id^="MainProduct-"]')) return;

      // Marker on <html> (ASAP) + the matching CSS, so even cards that
      // exist before the JS tagging pass are hidden by the :has()
      // fallback the instant the class lands.
      document.documentElement.classList.add(HTML_MARKER);
      ensureHardeningStyle();

      hardenPicker(document);

      // The #164 section swap (and Dawn's own variant re-render) replace
      // the picker subtree. Re-apply on any mutation under the product
      // section so freshly-injected 10L/15L cards are caught too.
      // Idempotent + cheap (only acts when .sybot-variant-card exist).
      if (!window.__sybaBHardenObserver) {
        var obs = new MutationObserver(function () {
          if (!isVariantB() || !isSybaSoilProduct()) return;
          hardenPicker(document);
        });
        obs.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
        window.__sybaBHardenObserver = obs;
        // Safety net: a few timed passes in case mutations are missed
        // (observer attaches after an early synchronous swap).
        var n = 0;
        var iv = setInterval(function () {
          n++;
          if (!isVariantB() || !isSybaSoilProduct()) {
            clearInterval(iv);
            return;
          }
          hardenPicker(document);
          if (n >= 20) clearInterval(iv); // ~5s @ 250ms
        }, 250);
      }
    } catch (e) {
      /* any failure → leave native PDP untouched (Control parity) */
    }
  }

  // Run the picker hardening as early as possible — synchronously on
  // script execution if the DOM is already parsed, else at the earliest
  // DOM event. It is gated, so non-B / non-SybaSoil is a strict no-op.
  function runHardeningASAP() {
    hardenVariantBPicker();
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        hardenVariantBPicker,
        { once: true }
      );
    }
  }
  runHardeningASAP();

  function run() {
    try {
      // --- Gate 0: in-flight / completed-swap guard ---------------------
      // Set true only AFTER all gates pass and just before the fetch, reset
      // on a recoverable failure. This closes the async race where two
      // near-simultaneous IIFE executions both clear Gate 3 before either
      // fetch settles. It is intentionally NOT a top-of-IIFE bail: the QA
      // flow re-inserts this <script> to re-run with a stubbed SybaIG, so
      // the gates (esp. Gate 2 / Gate 3) must be free to re-evaluate. A
      // genuinely completed swap is caught by Gate 3 (offer in DOM).
      if (window.__sybaPdpOfferInjectRan) return;

      // --- Gate 1: must be a product page -------------------------------
      var liveSection = document.querySelector('section[id^="MainProduct-"]');
      if (!liveSection) return; // not a PDP → no-op

      // --- Gate 2: SybaIG STARTED + Variant-B ONLY ----------------------
      // Mirrors assets/quick-add.js sybasoilIsVariantB() exactly. false for
      // Control / pending / SDK-not-ready / error → total no-op (Control
      // parity). The native PDP is never touched in those states.
      var isVariantB = false;
      try {
        isVariantB = !!(window.SybaIG && window.SybaIG.checkVariantBSync());
      } catch (e) {
        isVariantB = false;
      }
      if (!isVariantB) return;

      // --- Gate 2.5: SybaSoil product family ONLY -----------------------
      // Issue 1 follow-on (Control-parity leak found during verification):
      // run()'s only SybaSoil signal used to be "the ?view=sybasoil_offer_a-b
      // fetch came back containing [data-sybasoil-offer]" (swapSection line
      // ~454). That assumption is FALSE: Shopify's `?view=` parameter forces
      // the named alternate template render for ANY product, so fetching
      // ?view=sybasoil_offer_a-b on a NON-SybaSoil PDP (e.g. Perlite,
      // template suffix `soil_improvers`) returns HTTP 200 with the offer
      // block present — the swap validation passed and the SybaSoil offer
      // LEAKED onto a non-SybaSoil product for Variant-B visitors. This is a
      // pre-existing #164 defect (reproduced on the unmodified merged code),
      // but Issue 1's robust fix must not let the now-reliable IG wait widen
      // that leak. Gate it on the SAME strict, swap-independent template
      // signal the Issue-3 hardening already uses: window._template.suffix
      // must be in the `product.sybasoil*` family. This only ADDS a
      // precondition (Control / non-SybaSoil stay a strict no-op — native
      // PDP byte-for-byte untouched); it does NOT weaken or broaden any
      // existing gate. EN + NL Universal (and every real SybaSoil mix)
      // render on suffix `sybasoil`, so they are unaffected.
      try {
        if (!isSybaSoilProduct()) return;
      } catch (e) {
        return; // any uncertainty about the template → safe no-op
      }

      // --- Gate 3: idempotency ------------------------------------------
      // If the IG server-side Template Change fired, the offer is already
      // server-rendered. Never double-render / double-swap — but DO arm
      // the Issue-2 OOS→Klaviyo/tier resync watcher on the existing
      // server-rendered section (it's only ever attached once per
      // section instance and is a strict no-op outside Variant-B).
      if (document.querySelector('[data-sybasoil-offer], .sybasoil-offer')) {
        try { watchAvailabilityReswap(liveSection); } catch (e) { /* ignore */ }
        return;
      }

      // Claim the run before the async fetch so a second IIFE execution
      // (re-inserted <script>) bails at the top guard.
      window.__sybaPdpOfferInjectRan = true;

      // --- Build the Variant-B fetch URL --------------------------------
      // Mirror quick-add.js sybasoilFetchUrl(): append view=sybasoil_offer_a-b,
      // preserving any existing query string (notably ?variant=).
      var base = location.pathname + location.search;
      var url =
        base + (base.indexOf('?') === -1 ? '?' : '&') + 'view=sybasoil_offer_a-b';

      fetch(url)
        .then(function (response) {
          if (!response.ok) return null; // e.g. non-SybaSoil 404 → abort
          return response.text();
        })
        .then(function (responseText) {
          if (!responseText) {
            window.__sybaPdpOfferInjectRan = false; // allow a later retry
            return;
          }
          swapSection(responseText, liveSection);
        })
        .catch(function () {
          // network/parse failure → leave native PDP untouched. Reset the
          // guard so a subsequent legitimate run can retry.
          window.__sybaPdpOfferInjectRan = false;
        });
    } catch (e) {
      /* any failure → leave native PDP untouched (Control parity) */
    }
  }

  // Recreate <script> nodes inside `element` so they EXECUTE — element.innerHTML
  // does not run injected <script> children. Based on the technique from
  // assets/quick-add.js setInnerHTML(), with two adaptations REQUIRED for an
  // in-page section swap (quick-add does not need these because its modal is
  // a fresh DOM whose scripts never ran at global scope before):
  //
  //   1. INLINE scripts are wrapped in an IIFE before re-insertion. The
  //      standard PDP already executed this section's inline scripts at page
  //      load, declaring their top-level `const`/`let` (e.g.
  //      snippets/nf-mobile-sticky-add-to-cart.liquid's
  //      `const desktopFormWrapper`) in the GLOBAL scope. Re-running the
  //      verbatim source would throw `SyntaxError: Identifier '…' has already
  //      been declared`, which — being a parse-time throw that propagates out
  //      of forEach — aborts recreation of EVERY later script (the offer
  //      config + offer JS loader). Wrapping each inline body in
  //      `(function(){ … })()` makes those declarations function-scoped, so
  //      they no longer collide. The section's own globals are exported via
  //      explicit `window.*` assignment (window.sybasoil*, SybaPotCalc_init),
  //      which is unaffected by the IIFE wrap. External `src` scripts are
  //      re-inserted as-is (they self-guard via their own init flags).
  //
  //   2. Per-node try/catch (defence in depth) so a single problematic
  //      script can never prevent the remaining ones from being recreated.
  //
  //   3. The IIFE wrap is applied ONLY to genuinely EXECUTABLE inline
  //      scripts (no `type`, or a JavaScript / module MIME). DATA blocks
  //      — `<script type="application/json">` (Dawn's variant data, the
  //      product schema), `application/ld+json`, `text/template`, etc. —
  //      are NOT JavaScript: wrapping their body in `(function(){…})()`
  //      corrupts the payload so `JSON.parse` throws. That is exactly
  //      what broke variant changes on the swapped section: VariantSelects
  //      .getVariantData() parses the `application/json` block, so a
  //      wrapped data script left `currentVariant` unresolved and the
  //      product form's input[name=id] / price / ATC / offer tiers never
  //      updated on variant change (esp. selecting an OOS variant). Data
  //      scripts are therefore re-inserted with their body verbatim.
  function isExecutableScriptType(type) {
    if (!type) return true; // no type attr ⇒ classic JS
    var t = String(type).trim().toLowerCase();
    if (t === '') return true;
    if (t === 'module') return true;
    return (
      t === 'text/javascript' ||
      t === 'application/javascript' ||
      t === 'application/ecmascript' ||
      t === 'text/ecmascript' ||
      t === 'application/x-javascript'
    );
  }

  function setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach(function (oldScript) {
      try {
        var newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(function (attr) {
          newScript.setAttribute(attr.name, attr.value);
        });
        var hasSrc = oldScript.hasAttribute('src');
        var body = oldScript.innerHTML;
        var executable = isExecutableScriptType(
          oldScript.getAttribute('type')
        );
        if (!hasSrc && body && executable) {
          // Wrap EXECUTABLE inline bodies so top-level declarations are
          // function-scoped and cannot redeclare a global the standard
          // PDP already defined. Data scripts (application/json etc.) are
          // left verbatim — wrapping them would corrupt the payload.
          body = '(function(){\n' + body + '\n})();';
        }
        if (body) {
          newScript.appendChild(document.createTextNode(body));
        }
        oldScript.parentNode.replaceChild(newScript, oldScript);
      } catch (e) {
        /* one bad script must not abort recreation of the others */
      }
    });
  }

  function swapSection(responseText, liveSection) {
    try {
      var doc = new DOMParser().parseFromString(responseText, 'text/html');

      // The offer template's product section (same product + section.id as
      // the live one — Shopify duplicate-template preserves block IDs).
      var offerSection = doc.querySelector('section[id^="MainProduct-"]');
      if (!offerSection) return; // unexpected markup → abort, native PDP intact

      // Confirm this really is the SybaSoil offer template. A non-SybaSoil
      // product (or the standard template) won't contain the offer block;
      // abort silently and leave the native PDP untouched.
      if (!offerSection.querySelector('[data-sybasoil-offer], .sybasoil-offer')) {
        return;
      }

      // Re-check idempotency right before mutating (a server-side template
      // change could have hydrated between gate and fetch settle).
      if (document.querySelector('[data-sybasoil-offer], .sybasoil-offer')) return;

      // Mirror quick-add.js: copy color-/gradient classes from the fetched
      // section onto the live section wrapper so the offer template's color
      // scheme applies. (Same product+section so usually identical; copied
      // for parity / future-proofing.)
      offerSection.classList.forEach(function (cls) {
        if (cls.indexOf('color-') === 0 || cls === 'gradient') {
          liveSection.classList.add(cls);
        }
      });

      // Remove modal-only / duplicate-prone elements from the source BEFORE
      // injecting (mirrors quick-add.removeDOMElements). Unlike quick-add we
      // KEEP the media gallery, product title, breadcrumb and product form —
      // this is the real in-page PDP product area, not a stripped modal, so
      // customElementsQuickAddDrawer() is intentionally NOT applied (it would
      // gut the PDP). We only drop nodes that are pure duplicates / would
      // collide with the rest of the live page chrome.
      var pickup = offerSection.querySelector('pickup-availability');
      if (pickup) pickup.remove();

      // Swap the live section's content for the offer section's content,
      // recreating <script> nodes so inline config (window.sybasoil* globals,
      // the pot calculator) + the section's variant/gallery scripts +
      // assets/sybasoil-offer-tier-cards.js all execute and bind to the
      // freshly-injected product form. Element IDs come from the offer doc as
      // one internally-consistent set and uniquely replace the old ones — no
      // collision (the old section content is gone).
      setInnerHTML(liveSection, offerSection.innerHTML);

      // --- Re-init the same surfaces quick-add.js re-inits after a swap ---

      // KaChing: re-run the global initializer so its widgets re-scan the
      // freshly-injected product section (the offer template still includes
      // KaChing the same way the standard one does — no manual <kaching-bundle>
      // injection needed for an in-page section, only a re-init).
      try {
        if (typeof window.kachingBundlesInitialize === 'function') {
          window.kachingBundlesInitialize();
        }
      } catch (e) { /* KaChing optional — degrade silently */ }

      // Dynamic-checkout (Shop Pay etc.) buttons need re-init after Ajax.
      try {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } catch (e) { /* no payment buttons → ignore */ }

      // 3D / AR model viewer re-hydration.
      try {
        if (window.ProductModel && typeof window.ProductModel.loadShopifyXR === 'function') {
          window.ProductModel.loadShopifyXR();
        }
      } catch (e) { /* no XR models → ignore */ }

      // Accessibility: de-list the re-injected media gallery slider (mirrors
      // quick-add.removeGalleryListSemantic).
      try {
        var galleryList = liveSection.querySelector('[id^="Slider-Gallery"]');
        if (galleryList) {
          galleryList.setAttribute('role', 'presentation');
          galleryList
            .querySelectorAll('[id^="Slide-"]')
            .forEach(function (li) {
              li.setAttribute('role', 'presentation');
            });
        }
      } catch (e) { /* gallery optional → ignore */ }

      // Stop the variant picker from rewriting the URL/history on change.
      // The page already has ?variant= (or first-available); letting the
      // injected picker push history here would fight the address bar /
      // breadcrumb. Mirrors quick-add.preventVariantURLSwitching().
      try {
        var variantPicker = liveSection.querySelector(
          'variant-radios,variant-selects'
        );
        if (variantPicker) {
          variantPicker.setAttribute('data-update-url', 'false');
        }
      } catch (e) { /* no picker → ignore */ }

      // Preselect the 5L variant in the swapped section (mirrors
      // quick-add.preselect5L, simplified for the in-page context). The
      // offer template hides 10L/15L; the server may still have rendered
      // first_available (often 1L) when no ?variant= was on the URL. If a
      // 5L radio exists, is enabled, and isn't already the checked one,
      // flip it and dispatch `change` so variant-radios' cascade stamps the
      // form's hidden input[name=id] to 5L (so ATC + the offer module's
      // recalc both see 5L). No-op when the URL already carried ?variant=
      // (server rendered the requested variant) or there's no 5L option.
      try {
        preselect5L(liveSection);
      } catch (e) { /* preselect is best-effort → ignore */ }

      // Issue 2 — keep the buy area + offer tiers correct when the
      // shopper later selects an OUT-OF-STOCK variant in the swapped
      // section (offer template uses hide_unavailable:false, so OOS
      // variants are shown and clickable).
      try {
        watchAvailabilityReswap(liveSection);
      } catch (e) { /* best-effort → ignore */ }
    } catch (e) {
      /* any failure → the section may be partially built; the product form
         and offer JS degrade gracefully. Native PDP was already replaced
         only if we got far enough; earlier errors leave it untouched. */
    }
  }

  // ===================================================================
  // Issue 2 — OOS variant → Klaviyo "notify me" + offer-tier resync
  // -------------------------------------------------------------------
  // The Klaviyo-back-in-stock vs Add-to-Cart toggle is a SERVER-rendered
  // Liquid decision in sections/main-product.liquid:
  //   {% if product.selected_or_first_available_variant.available == false
  //         or quantity_rule_soldout %}
  //     {% render 'klaviyo-back-in-stock-btn' %}
  //   {% else %}
  //     {% render 'buy-buttons' %} ...
  //   {% endif %}
  // Dawn's client-side variant change (global.js renderProductInfo) only
  // patches price / sku / inventory / submit-button-disabled — it NEVER
  // re-renders the buy_buttons block, so selecting a sold-out variant
  // leaves the Add-to-Cart form in place and the Klaviyo form never
  // appears (a pre-existing theme limitation the offer must not inherit).
  // The offer tier cards also won't recalc for a variant the offer
  // module didn't observe a propagated change for.
  //
  // Fix: after the #164 swap, watch variant changes. When the selected
  // variant's availability (read from the picker's embedded variant
  // JSON) no longer matches what the buy area currently shows
  // (ATC visible but variant is OOS, or Klaviyo visible but variant is
  // back in stock), re-fetch the offer section for THAT variant and
  // re-run the same full section swap. The server then renders the
  // correct buy_buttons branch (Klaviyo or ATC) AND the offer block for
  // the new variant in one consistent pass — no client re-implementation
  // of the server's sold-out logic (no dual-write drift).
  //
  // Strictly scoped: only ever runs inside an already-swapped Variant-B
  // offer section. The double-add guard in
  // assets/sybasoil-offer-tier-cards.js is on document-delegated
  // listeners + window flags, so it survives the re-swap untouched.
  // Idempotent + debounced; degrades to leaving the section as-is on any
  // error.
  function getPickerVariantData(scope) {
    try {
      var vs = scope.querySelector('variant-radios, variant-selects');
      if (!vs) return null;
      var json = vs.querySelector('script[type="application/json"]');
      if (!json) return null;
      return JSON.parse(json.textContent);
    } catch (e) {
      return null;
    }
  }

  function selectedVariantId(scope) {
    try {
      var form = scope.querySelector('form[action*="/cart/add"]');
      var input = form && form.querySelector('input[name="id"]');
      if (input && input.value) return parseInt(input.value, 10);
    } catch (e) { /* ignore */ }
    return null;
  }

  function buyAreaShowsKlaviyo(scope) {
    return !!scope.querySelector('.klaviyo-bis-trigger');
  }

  function watchAvailabilityReswap(scope) {
    if (!scope) return;
    // Bind the document-delegated change listeners exactly ONCE for the
    // whole page lifetime. The MainProduct <section> element itself is
    // stable across re-swaps (setInnerHTML only replaces its innerHTML),
    // and a re-swap re-calls this function — binding per call would leak
    // a duplicate global `change` listener (→ duplicate re-swaps) every
    // time. The window guard makes re-arming a safe no-op; the swap
    // logic always re-queries the LIVE section/form/picker so a single
    // set of listeners keeps working after any number of re-swaps.
    if (window.__sybaAvailWatchBound) return;
    window.__sybaAvailWatchBound = true;

    function maybeReswap() {
      try {
        if (window.__sybaReswapInFlight) return;
        // Still Variant-B + offer present? (defence in depth)
        if (!isVariantB()) return;
        if (!document.querySelector('[data-sybasoil-offer], .sybasoil-offer')) return;

        var id = selectedVariantId(document);
        if (!id) return;
        var data = getPickerVariantData(document);
        if (!data || !data.length) return;
        var v = null;
        for (var i = 0; i < data.length; i++) {
          if (parseInt(data[i].id, 10) === id) { v = data[i]; break; }
        }
        if (!v) return;

        var isAvail = v.available === true;
        var showsKlaviyo = buyAreaShowsKlaviyo(document);

        // Mismatch: ATC shown for an OOS variant, OR Klaviyo shown for
        // an in-stock variant. Either way the server must re-render the
        // buy_buttons branch + the offer block for THIS variant.
        if (isAvail === showsKlaviyo) {
          window.__sybaReswapInFlight = true;
          var liveSection = document.querySelector('section[id^="MainProduct-"]');
          if (!liveSection) { window.__sybaReswapInFlight = false; return; }

          var path = location.pathname;
          var url =
            path + '?variant=' + id + '&view=sybasoil_offer_a-b';

          fetch(url)
            .then(function (r) { return r.ok ? r.text() : null; })
            .then(function (txt) {
              if (!txt) { window.__sybaReswapInFlight = false; return; }
              // Allow swapSection's idempotency re-check to pass: the
              // offer is currently in the DOM (from the prior swap), so
              // clear the completed-swap guard and let swapSection
              // replace the section content with the new variant's
              // server render. swapSection re-validates it really is the
              // SybaSoil offer template before mutating.
              var fresh = new DOMParser().parseFromString(txt, 'text/html');
              var freshSection = fresh.querySelector('section[id^="MainProduct-"]');
              if (
                !freshSection ||
                !freshSection.querySelector('[data-sybasoil-offer], .sybasoil-offer')
              ) {
                // Not the offer template (e.g. unexpected redirect) →
                // leave the current section as-is.
                window.__sybaReswapInFlight = false;
                return;
              }
              // Re-init guards: SybasoilOffer_initialized stays true
              // (document-delegated listeners + the double-add guard are
              // intentionally preserved across the re-swap). Replace the
              // section content in place.
              setInnerHTML(liveSection, freshSection.innerHTML);
              try {
                if (typeof window.kachingBundlesInitialize === 'function') {
                  window.kachingBundlesInitialize();
                }
              } catch (e) { /* ignore */ }
              try {
                if (window.Shopify && Shopify.PaymentButton) {
                  Shopify.PaymentButton.init();
                }
              } catch (e) { /* ignore */ }
              try {
                var vp = liveSection.querySelector('variant-radios,variant-selects');
                if (vp) vp.setAttribute('data-update-url', 'false');
              } catch (e) { /* ignore */ }
              // Re-apply Issue-3 hardening to the freshly injected picker
              // (idempotent; gated internally). The availability watcher
              // does NOT need re-arming — its listeners are bound once
              // globally and always re-query the live section.
              try { hardenPicker(document); } catch (e) { /* ignore */ }
              // Rebind the offer tier maths to the FRESH section DOM.
              // assets/sybasoil-offer-tier-cards.js binds `root`/`ctx`
              // once at SybasoilOffer_init and early-returns on re-init
              // (window.SybasoilOffer_initialized stays true — which is
              // correct: re-running init would double-bind its
              // document-delegated listeners + is unnecessary). But that
              // means its captured `root` now points at the detached
              // pre-re-swap node, so its own change-listener recalc would
              // paint the OLD (removed) tier DOM. Drive the recalc
              // explicitly against the freshly-injected root using the
              // module's own public helpers, after stamping the new
              // variant id so checkVariantChange/recalcAll resolve it.
              // This touches ONLY presentational tier state — the
              // double-add guard (window.SybasoilOffer__adding /
              // __lastAdd, the submit chokepoint) is untouched.
              // Emit the canonical event FIRST so the offer module's own
              // (stale-root) listeners run before our authoritative
              // repaint below — otherwise their late recalc on the
              // detached node, plus their form-read early-return when the
              // buy area is now Klaviyo (no form → lastVariantId left at
              // the old value), would clobber our result ordering.
              try {
                document.dispatchEvent(new CustomEvent('variant:change'));
              } catch (e) { /* ignore */ }
              // Rebind the offer tier maths to the FRESH section DOM.
              // Retry a few times: the recreated inline config <script>
              // (rebuilds window.sybasoilVariants) and the offer module's
              // own delegated listeners settle asynchronously and in a
              // non-deterministic order relative to this callback. Each
              // pass re-stamps the new variant id and repaints the LIVE
              // root, so the final visible state is always the requested
              // variant regardless of who ran last. Presentational only —
              // the double-add guard state is never touched.
              try {
                if (
                  typeof window.SybasoilOffer_recalcAll === 'function' &&
                  typeof window.SybasoilOffer_readContext === 'function'
                ) {
                  var rcTries = 0;
                  var rcIv = setInterval(function () {
                    rcTries++;
                    try {
                      var newRoot = document.querySelector(
                        '[data-sybasoil-offer]'
                      );
                      if (
                        newRoot &&
                        window.sybasoilVariants &&
                        window.sybasoilVariants[id]
                      ) {
                        var newCtx =
                          window.SybasoilOffer_readContext(newRoot);
                        if (newCtx) {
                          window.SybasoilOffer_lastVariantId = String(id);
                          window.SybasoilOffer_recalcAll(newRoot, newCtx);
                        }
                      }
                    } catch (e) { /* ignore */ }
                    if (rcTries >= 8) clearInterval(rcIv); // ~0.8s
                  }, 100);
                }
              } catch (e) { /* ignore */ }
              window.__sybaReswapInFlight = false;
            })
            .catch(function () { window.__sybaReswapInFlight = false; });
        }
      } catch (e) {
        window.__sybaReswapInFlight = false;
      }
    }

    // Debounced — variant-radios fires several DOM updates per change.
    var deb;
    function schedule() {
      clearTimeout(deb);
      deb = setTimeout(maybeReswap, 120);
    }

    // input[name=id] is the authoritative post-cascade variant signal
    // (global.js updateVariantInput stamps it then dispatches `change`).
    document.addEventListener('change', function (e) {
      if (
        e.target &&
        e.target.matches &&
        e.target.matches(
          'variant-radios input, variant-selects select, input[name="id"]'
        )
      ) {
        schedule();
      }
    });
    document.addEventListener('variant:change', schedule);
    // Initial reconciliation (covers a sold-out variant arriving via
    // ?variant= on first load after the swap).
    schedule();
  }

  function preselect5L(scope) {
    var variantRadios = scope.querySelector('variant-radios');
    if (!variantRadios) return;

    var radios = variantRadios.querySelectorAll('input[type="radio"]');
    var target = null;
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      var token = (r.value || '').trim().split(/\s+/)[0].toLowerCase();
      if (token === '5l' && !r.disabled) {
        target = r;
        break;
      }
    }
    if (!target || target.checked) return;

    var fieldset = target.closest('fieldset');
    if (fieldset) {
      fieldset
        .querySelectorAll('input[type="radio"]:checked')
        .forEach(function (r) {
          r.checked = false;
        });
    }
    target.checked = true;

    if (
      window.customElements &&
      typeof customElements.upgrade === 'function'
    ) {
      customElements.upgrade(scope);
    }

    // Defer so custom elements upgrade + the section's scripts finish wiring
    // their change listeners before we dispatch (same reasoning as
    // quick-add.preselect5L's setTimeout(0)).
    setTimeout(function () {
      try {
        target.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (e) { /* ignore */ }
    }, 0);
  }

  // ===================================================================
  // Issue 1 — wait for the async IG SDK before giving up (Variant-B
  //           shoppers on heavier / localized SybaSoil PDPs saw Control)
  // -------------------------------------------------------------------
  // run()'s Gate 2 reads window.SybaIG.checkVariantBSync() SYNCHRONOUSLY.
  // syba-ig-test-state.js returns false whenever the Intelligems SDK has
  // not resolved yet (resolveVariation() === undefined). The IG bundle is
  // loaded <script type="module" async> in layout/theme.liquid, so it can
  // (and on heavier pages — e.g. the localized /nl-nl/ Universal Mix PDP,
  // which carries the full market-localized payload — routinely does)
  // finish AFTER DOMContentLoaded. The previous trigger sampled Gate 2
  // exactly once at DOMContentLoaded with no retry: if IG had not resolved
  // by then, run() returned at Gate 2 and the injector PERMANENTLY no-ops,
  // so a genuinely Variant-B visitor was served the native Control PDP.
  // quick-add.js does not hit this because it evaluates the same gate
  // lazily on a user click (seconds later, IG long since resolved); the
  // PDP injector had no second chance.
  //
  // Fix: keep run()'s synchronous Gate 2 exactly as-is (fast path — when
  // IG is already resolved B this still works on the very first call and
  // nothing changes). ADDITIONALLY, if that first call did not complete a
  // swap AND no server-side offer is present, defer to the SDK via the
  // already-shipped SybaIG.whenResolved() (waits up to ~5s, reports
  // 'variant-b' | 'control' | 'inactive') and retry run() ONCE only when
  // the resolved state is definitively 'variant-b'. 'control' / 'inactive'
  // never re-call run() → Control parity is byte-for-byte preserved. run()
  // is idempotent (Gate 0 __sybaPdpOfferInjectRan + Gate 3 offer-in-DOM),
  // so the extra call can never double-fetch or double-swap, and the
  // double-add guard in sybasoil-offer-tier-cards.js is untouched.
  function runWithIGWait() {
    try {
      run();
    } catch (e) { /* run() is self-guarded; never throw to the trigger */ }

    try {
      // Already handled (swap done / in-flight, or server-side offer
      // present) → nothing to wait for.
      if (window.__sybaPdpOfferInjectRan) return;
      if (document.querySelector('[data-sybasoil-offer], .sybasoil-offer')) {
        return;
      }
      // Only worth waiting on an actual PDP. Non-product pages stay a
      // strict no-op (run() already bailed at Gate 1).
      if (!document.querySelector('section[id^="MainProduct-"]')) return;
      // If the SDK is already resolved, run()'s synchronous Gate 2 has
      // its definitive answer (B → it already proceeded above; Control /
      // inactive → it correctly no-opped). No async wait needed; do NOT
      // re-call run() so Control parity stays absolute.
      if (
        !window.SybaIG ||
        typeof window.SybaIG.whenResolved !== 'function'
      ) {
        return;
      }
      var resolvedNow;
      try {
        // checkVariantBSync()/checkControlSync() are both false ONLY while
        // the SDK is still unresolved (undefined). If either is true the
        // SDK has resolved and run() already acted correctly.
        resolvedNow =
          window.SybaIG.checkVariantBSync() ||
          window.SybaIG.checkControlSync();
      } catch (e) {
        resolvedNow = false;
      }
      if (resolvedNow) return;

      // SDK genuinely not resolved yet — wait for it, then retry run()
      // exactly once and ONLY for a definitive Variant-B. Control /
      // inactive deliberately do nothing (native PDP untouched).
      window.SybaIG.whenResolved(function (state) {
        if (state !== 'variant-b') return; // strict no-op for control/inactive
        try {
          run();
        } catch (e) { /* self-guarded */ }
      });
    } catch (e) {
      /* any failure → leave native PDP untouched (Control parity) */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runWithIGWait);
  } else {
    runWithIGWait();
  }
})();
